import React, { useState } from 'react';
import { createRiskCate } from '../../api/riskCateApi';
import {
  CATEGORY_TYPE_OPTIONS, PROCESS_LIST,
} from '../../utils/riskUtils';
import {
  Box, Typography, TextField, Button, Stack, Paper, Divider,
  MenuItem, InputAdornment, FormGroup, FormControlLabel, Checkbox,
} from '@mui/material';
import {
  AddCircle as AddIcon,
  Tag as TagIcon,
  Sort as SortIcon,
  AccountTree as AccountTreeIcon,
  Folder as FolderIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';

const initForm = {
  risk_category_num: '',
  risk_category_context: '',
  category_type: '',
  parent_id: '',
  sort_order: 0,
};

function RiskCateForm({ token, comCode, allItems, onCancel, onCreated }) {
  const [form, setForm] = useState(initForm);
  const [selectedProcesses, setSelectedProcesses] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const folderItems = allItems.filter(item => item.category_type === 'folder');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'category_type' ? { parent_id: '' } : {}),
    }));
    if (name === 'category_type') setSelectedProcesses([]);
  };

  const handleProcessToggle = (key) => {
    setSelectedProcesses(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleSubmit = async () => {
    if (!form.risk_category_num.trim()) {
      alert('분류 넘버를 입력하세요.');
      return;
    }
    if (!form.risk_category_context.trim()) {
      alert('분류 내용을 입력하세요.');
      return;
    }
    if (!form.category_type) {
      alert('타입을 선택하세요.');
      return;
    }
    if (form.category_type === 'text' && !form.parent_id) {
      alert('텍스트 타입은 상위 분류를 선택해야 합니다.');
      return;
    }
    setSubmitting(true);
    try {
      await createRiskCate(
        {
          ...form,
          parent_id:    form.parent_id !== '' ? Number(form.parent_id) : null,
          sort_order:   Number(form.sort_order),
          process_list: selectedProcesses.length > 0 ? selectedProcesses.join('^') : null,
        },
        token,
        comCode,
      );
      alert('등록되었습니다.');
      if (onCreated) onCreated();
    } catch (err) {
      alert('등록 실패: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e0e4e8', bgcolor: '#fff' }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        mb: 3, pb: 1.5, borderBottom: '2px solid #2e7d32',
      }}>
        <AddIcon sx={{ color: '#2e7d32', fontSize: '1.8rem' }} />
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
          위험성 분류 등록
        </Typography>
      </Box>

      <Stack spacing={2.5}>
        {/* 분류 넘버 */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057', display: 'flex', alignItems: 'center' }}>
            분류 넘버 <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
          </Typography>
          <TextField
            fullWidth size="small"
            name="risk_category_num"
            value={form.risk_category_num}
            onChange={handleChange}
            placeholder="분류 넘버를 입력하세요"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <TagIcon fontSize="small" sx={{ color: '#adb5bd' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* 분류 내용 */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057', display: 'flex', alignItems: 'center' }}>
            분류 내용 <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
          </Typography>
          <TextField
            fullWidth size="small" multiline rows={3}
            name="risk_category_context"
            value={form.risk_category_context}
            onChange={handleChange}
            placeholder="분류 내용을 입력하세요"
          />
        </Box>

        {/* 타입 */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057', display: 'flex', alignItems: 'center' }}>
            타입 <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
          </Typography>
          <TextField
            select fullWidth size="small"
            name="category_type"
            value={form.category_type}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FolderIcon fontSize="small" sx={{ color: '#adb5bd' }} />
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value="">선택</MenuItem>
            {CATEGORY_TYPE_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
        </Box>

        {/* 평가 공정 - 텍스트 타입일 때만 표시 */}
        <Box sx={{ display: form.category_type === 'text' ? 'block' : 'none' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AssignmentIcon fontSize="small" sx={{ color: '#adb5bd' }} />
            평가 공정
          </Typography>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, px: 2, py: 1 }}>
            <FormGroup>
              {Object.entries(PROCESS_LIST).map(([key, label]) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedProcesses.includes(key)}
                      onChange={() => handleProcessToggle(key)}
                    />
                  }
                  label={<Typography variant="body2">{label}</Typography>}
                />
              ))}
            </FormGroup>
          </Box>
        </Box>

        {/* 상위 분류 */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057', display: 'flex', alignItems: 'center' }}>
            상위 분류
            {form.category_type === 'text' && (
              <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
            )}
          </Typography>
          <TextField
            select fullWidth size="small"
            name="parent_id"
            value={form.parent_id}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccountTreeIcon fontSize="small" sx={{ color: '#adb5bd' }} />
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value="">없음 (최상위)</MenuItem>
            {folderItems.map(item => (
              <MenuItem key={item.id} value={item.id}>
                {item.risk_category_num} - {item.risk_category_context}
              </MenuItem>
            ))}
          </TextField>
          {form.category_type === 'folder' && (
            <Typography variant="caption" sx={{ color: '#6c757d', mt: 0.5, display: 'block' }}>
              폴더 타입의 상위 분류 목록만 표시됩니다.
            </Typography>
          )}
          {form.category_type === 'text' && (
            <Typography variant="caption" sx={{ color: '#dc3545', mt: 0.5, display: 'block' }}>
              텍스트 타입은 상위 분류 선택이 필수입니다.
            </Typography>
          )}
        </Box>

        {/* 정렬 순서 */}
        <Box sx={{ maxWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#495057' }}>
            정렬 순서
          </Typography>
          <TextField
            fullWidth size="small" type="number"
            name="sort_order"
            value={form.sort_order}
            onChange={handleChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SortIcon fontSize="small" sx={{ color: '#adb5bd' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Divider sx={{ my: 1 }} />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="contained" color="success"
            onClick={handleSubmit}
            disabled={submitting}
            sx={{ px: 4, fontWeight: 'bold' }}
          >
            {submitting ? '등록 중...' : '저장하기'}
          </Button>
          <Button variant="outlined" color="secondary" onClick={onCancel} sx={{ px: 3 }}>
            취소
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default RiskCateForm;
